/**
 * Direct Checkout Handler for Shopify
 * Permite redirecionamento direto para checkout ao clicar em produtos
 * 
 * @author New Capelli
 * @version 1.0.0
 */

class DirectCheckoutHandler {
  constructor() {
    this.selectors = {
      productCard: '.product-card',
      productImage: '.product-card img',
      buyNowButton: '.btn-buy-now',
      addToCartButton: '.btn-add-to-cart'
    };
    
    this.config = {
      clearCartBeforeAdd: true, // Limpar carrinho antes de adicionar
      showLoadingAnimation: true,
      redirectDelay: 100, // Delay antes do redirecionamento
      errorDisplayTime: 3000, // Tempo para mostrar erro
      fallbackToProductPage: true
    };
    
    this.init();
  }

  init() {
    // Handle product image clicks
    document.addEventListener('click', (e) => {
      const productImage = e.target.closest(this.selectors.productImage);
      if (productImage) {
        e.preventDefault();
        this.handleDirectCheckout(productImage);
      }
    });

    // Handle buy now button clicks
    document.addEventListener('click', (e) => {
      const buyNowBtn = e.target.closest(this.selectors.buyNowButton);
      if (buyNowBtn) {
        e.preventDefault();
        this.handleBuyNowButton(buyNowBtn);
      }
    });

    // Handle add to cart button clicks (with checkout option)
    document.addEventListener('click', (e) => {
      const addToCartBtn = e.target.closest(this.selectors.addToCartButton);
      if (addToCartBtn && addToCartBtn.dataset.checkout === 'true') {
        e.preventDefault();
        this.handleAddToCartWithCheckout(addToCartBtn);
      }
    });
  }

  /**
   * Handle direct checkout from product image
   */
  async handleDirectCheckout(imageElement) {
    const variantId = imageElement.dataset.variantId;
    const productTitle = imageElement.dataset.productTitle;
    const productHandle = imageElement.closest(this.selectors.productCard)?.dataset.productHandle;
    
    if (!variantId) {
      console.error('Variant ID not found');
      this.showError('ID da variante não encontrado');
      return;
    }

    try {
      this.showLoadingState(imageElement, 'Adicionando ao carrinho...');

      // Clear cart if configured
      if (this.config.clearCartBeforeAdd) {
        await this.clearCart();
      }

      // Add product to cart
      const cartResponse = await this.addToCart(parseInt(variantId), 1);
      
      if (!cartResponse.success) {
        throw new Error(cartResponse.error || 'Erro ao adicionar produto ao carrinho');
      }

      // Show success message
      this.showSuccessState(imageElement, 'Produto adicionado! Redirecionando...');

      // Redirect to checkout
      setTimeout(() => {
        window.location.href = '/cart';
      }, this.config.redirectDelay);

    } catch (error) {
      console.error('Direct checkout error:', error);
      this.showErrorState(imageElement, error.message);
      
      // Fallback to product page
      if (this.config.fallbackToProductPage && productHandle) {
        setTimeout(() => {
          window.location.href = `/products/${productHandle}`;
        }, this.config.errorDisplayTime);
      }
    } finally {
      this.hideLoadingState(imageElement);
    }
  }

  /**
   * Handle buy now button clicks
   */
  async handleBuyNowButton(button) {
    const variantId = button.dataset.variantId || 
                     button.closest('form')?.querySelector('input[name="id"]')?.value;
    
    if (!variantId) {
      console.error('Variant ID not found for buy now button');
      return;
    }

    try {
      this.showButtonLoadingState(button, 'Processando...');

      // Clear cart and add product
      await this.clearCart();
      const cartResponse = await this.addToCart(parseInt(variantId), 1);
      
      if (!cartResponse.success) {
        throw new Error(cartResponse.error);
      }

      // Redirect to checkout
      window.location.href = '/cart';

    } catch (error) {
      console.error('Buy now error:', error);
      this.showButtonErrorState(button, error.message);
    } finally {
      this.hideButtonLoadingState(button);
    }
  }

  /**
   * Handle add to cart with immediate checkout
   */
  async handleAddToCartWithCheckout(button) {
    const form = button.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = formData.get('quantity') || 1;

    if (!variantId) {
      console.error('Variant ID not found in form');
      return;
    }

    try {
      this.showButtonLoadingState(button, 'Adicionando...');

      const cartResponse = await this.addToCart(parseInt(variantId), parseInt(quantity));
      
      if (!cartResponse.success) {
        throw new Error(cartResponse.error);
      }

      // Redirect to checkout
      window.location.href = '/cart';

    } catch (error) {
      console.error('Add to cart with checkout error:', error);
      this.showButtonErrorState(button, error.message);
    } finally {
      this.hideButtonLoadingState(button);
    }
  }

  /**
   * Add product to cart via AJAX
   */
  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity
          }]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.description || 'Erro ao adicionar ao carrinho'
        };
      }

      const cartData = await response.json();
      return {
        success: true,
        data: cartData
      };

    } catch (error) {
      return {
        success: false,
        error: 'Erro de conexão'
      };
    }
  }

  /**
   * Clear cart contents
   */
  async clearCart() {
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.warn('Failed to clear cart, continuing with existing items');
      }
    } catch (error) {
      console.warn('Error clearing cart:', error);
    }
  }

  /**
   * Show loading state on element
   */
  showLoadingState(element, message = 'Carregando...') {
    if (!this.config.showLoadingAnimation) return;

    element.style.opacity = '0.7';
    element.style.pointerEvents = 'none';
    
    const loadingDiv = document.createElement('div');
    loadingDiv.className = 'direct-checkout-loading';
    loadingDiv.innerHTML = `
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>${message}</p>
      </div>
    `;
    
    this.addLoadingStyles(loadingDiv);
    
    element.style.position = 'relative';
    element.parentElement.appendChild(loadingDiv);
  }

  /**
   * Hide loading state
   */
  hideLoadingState(element) {
    element.style.opacity = '';
    element.style.pointerEvents = '';
    
    const loadingIndicator = element.parentElement.querySelector('.direct-checkout-loading');
    if (loadingIndicator) {
      loadingIndicator.remove();
    }
  }

  /**
   * Show success state
   */
  showSuccessState(element, message) {
    const successDiv = document.createElement('div');
    successDiv.className = 'direct-checkout-success';
    successDiv.innerHTML = `
      <div class="success-message">
        <p>✅ ${message}</p>
      </div>
    `;
    
    this.addSuccessStyles(successDiv);
    element.parentElement.appendChild(successDiv);
    
    setTimeout(() => {
      if (successDiv.parentElement) {
        successDiv.remove();
      }
    }, 2000);
  }

  /**
   * Show error state
   */
  showErrorState(element, message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'direct-checkout-error';
    errorDiv.innerHTML = `
      <div class="error-message">
        <p>❌ ${message}</p>
      </div>
    `;
    
    this.addErrorStyles(errorDiv);
    element.parentElement.appendChild(errorDiv);
    
    setTimeout(() => {
      if (errorDiv.parentElement) {
        errorDiv.remove();
      }
    }, this.config.errorDisplayTime);
  }

  /**
   * Show button loading state
   */
  showButtonLoadingState(button, text) {
    button.dataset.originalText = button.textContent;
    button.textContent = text;
    button.disabled = true;
    button.style.opacity = '0.7';
  }

  /**
   * Hide button loading state
   */
  hideButtonLoadingState(button) {
    button.textContent = button.dataset.originalText || 'Comprar Agora';
    button.disabled = false;
    button.style.opacity = '';
  }

  /**
   * Show button error state
   */
  showButtonErrorState(button, message) {
    button.textContent = 'Erro';
    button.style.backgroundColor = '#d32f2f';
    
    setTimeout(() => {
      this.hideButtonLoadingState(button);
      button.style.backgroundColor = '';
    }, 2000);
  }

  /**
   * Show general error message
   */
  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'direct-checkout-global-error';
    errorDiv.innerHTML = `
      <div class="error-message">
        <p>❌ ${message}</p>
      </div>
    `;
    
    this.addGlobalErrorStyles(errorDiv);
    document.body.appendChild(errorDiv);
    
    setTimeout(() => {
      if (errorDiv.parentElement) {
        errorDiv.remove();
      }
    }, 3000);
  }

  /**
   * Add loading styles
   */
  addLoadingStyles(element) {
    element.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(255, 255, 255, 0.95);
      padding: 20px;
      border-radius: 8px;
      z-index: 1000;
      text-align: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;
  }

  /**
   * Add success styles
   */
  addSuccessStyles(element) {
    element.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(76, 175, 80, 0.1);
      color: #2e7d32;
      padding: 15px;
      border-radius: 8px;
      z-index: 1000;
      text-align: center;
      border: 2px solid #4caf50;
    `;
  }

  /**
   * Add error styles
   */
  addErrorStyles(element) {
    element.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(244, 67, 54, 0.1);
      color: #d32f2f;
      padding: 15px;
      border-radius: 8px;
      z-index: 1000;
      text-align: center;
      border: 2px solid #f44336;
    `;
  }

  /**
   * Add global error styles
   */
  addGlobalErrorStyles(element) {
    element.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f44336;
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      animation: slideIn 0.3s ease;
    `;
  }
}

// Initialize the handler when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DirectCheckoutHandler();
  });
} else {
  new DirectCheckoutHandler();
}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  .loading-spinner .spinner {
    width: 30px;
    height: 30px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 10px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .product-card {
    position: relative;
    transition: transform 0.2s ease;
  }

  .product-card:hover {
    transform: scale(1.02);
  }

  .direct-checkout-loading,
  .direct-checkout-success,
  .direct-checkout-error {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.4;
  }
`;

document.head.appendChild(style); 